import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import { useState } from "react";
import { DEFAULT_LANDING_BANNER, GIFT_ENVELOPE_ICON } from "@/constants/campaignBuilder";
import {
  DEFAULT_LANDING_HEADLINE,
  DEFAULT_LANDING_SUBTITLE,
  type HeroTextMode,
} from "@/constants/landing";
import type { RedemptionConfig } from "@/interfaces/redemption";
import { youTubeThumbnailUrl } from "@/utils/video-helper";

interface RedemptionHeroProps {
  config: RedemptionConfig;
  isMobile: boolean;
  /** Opens the gift-message modal (owned higher up, in Redemption). */
  onReadMessage: () => void;
  /** Opens the gift video-message modal (owned higher up, in Redemption). */
  onPlayVideo: () => void;
  /** Hero headline; defaults to the recipient copy. Editable in the campaign builder. */
  headline?: string;
  /** Hero subtitle; defaults to the recipient copy. Editable in the campaign builder. */
  subtitle?: string;
  /** Hero surface color (Pro custom colors); defaults to the clear-dust token. */
  bgColor?: string;
  /** Hero text contrast (Pro custom colors): "light" for a dark hero background. */
  textMode?: HeroTextMode;
}

// Shared banner-card frame (16:9, dark placeholder) + its cover image. Kept as
// literal class strings so Tailwind's scanner picks them up.
const BANNER_BOX =
  "relative flex aspect-video w-full items-center justify-center overflow-hidden bg-rodeo-black";
const BANNER_IMG = "absolute inset-0 z-1 h-full w-full object-cover";

/**
 * Redemption landing hero — the branded top of the landing/collection page: a
 * hardcoded headline + subtitle, a "Gift Message" invitation (if
 * `showGiftMessage`), and a banner card (if `showBanner || showVideo`) that plays
 * the gift video on tap when `showVideo`. Ported from the prototype's `.lp-hero*`
 * (collection.css) to Tailwind + design tokens. Reused by the recipient landing
 * and (later) the campaign builder's Landing preview tab.
 */
export default function RedemptionHero({
  config,
  isMobile,
  onReadMessage,
  onPlayVideo,
  headline = DEFAULT_LANDING_HEADLINE,
  subtitle = DEFAULT_LANDING_SUBTITLE,
  bgColor,
  textMode = "dark",
}: RedemptionHeroProps) {
  const showHeroRight = config.showVideo || config.showBanner;
  const light = textMode === "light";
  // The envelope glyph is a solid PNG; recolor it by using it as a CSS mask over a
  // background fill (campaign "Gift Message Envelope" color; default = envelope dark).
  // NOTE: a mask is a single alpha channel, so the whole glyph takes one flat color
  // (no envelope-vs-paper two-tone — that would need a vector, i.e. a different icon).
  const envelopeColor = config.envelopeColor || "var(--color-rodeo-black)";
  const bannerSrc = config.bannerUrl || DEFAULT_LANDING_BANNER;
  // Play card: the YouTube thumbnail when the link is YouTube; if it fails to load, the banner —
  // once (keyed on the failed src, so a different link gets its own attempt).
  const [failedThumb, setFailedThumb] = useState<string | null>(null);
  const thumbSrc = config.showVideo ? youTubeThumbnailUrl(config.videoUrl) : undefined;
  const playCardSrc = thumbSrc && thumbSrc !== failedThumb ? thumbSrc : bannerSrc;
  // Solo hero — banner + video both off. With no media to pair against, the copy
  // becomes a centered text composition with more vertical presence (prototype's
  // `.lp-hero--solo`, desktop/tablet only; mobile keeps the standard padding).
  const solo = !showHeroRight;

  // Mobile drops horizontal padding so the banner goes full-bleed; desktop solo
  // gets extra vertical presence. NOTE: the mobile top padding (`py-8.5` = 34px)
  // is cancelled by the banner block's `-mt-8.5` below to pull it flush under the
  // header — keep those two values in sync.
  const sectionPad = isMobile ? "px-0 py-8.5" : solo ? "px-12 pt-19 pb-15" : "px-12 pt-14 pb-11";

  return (
    <section
      className={`relative flex justify-center overflow-hidden bg-clear-dust ${sectionPad}`}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      <div
        className={`flex w-full ${
          isMobile ? "flex-col items-stretch gap-5.5" : "max-w-230 flex-row items-center gap-10"
        }`}
      >
        {/* Left — copy + gift-message invitation. With no banner/video beside it,
            or on mobile where the banner stacks above instead of beside it,
            the copy is effectively the whole hero, so center it instead of hugging the left.
            Phone: the banner is full-bleed, so the copy gets its own horizontal
            padding + a capped, centered measure; gap-based rhythm means an absent
            element (e.g. no gift message) leaves no dangling margin. */}
        <div
          className={`flex flex-1 flex-col ${
            showHeroRight && !isMobile ? "items-start text-left" : "items-center text-center"
          } ${isMobile ? "mx-auto box-border max-w-130 gap-4 px-6" : ""}`}
        >
          <h1
            className={`font-crimson font-normal ${light ? "text-white" : "text-rodeo-black"} ${
              isMobile ? "m-0 text-[26px]" : "mb-4 text-[34px]"
            }`}
          >
            {headline}
          </h1>
          <p
            className={`font-lato text-[15px] leading-[1.7] ${
              light ? "text-smoke" : "text-graphite"
            } ${isMobile ? "m-0" : "mb-9"} ${solo ? "max-w-120 text-balance" : "max-w-105"} ${
              // Phones only (R12): balance the line breaks so the last line never orphans one word.
              isMobile ? "text-balance" : ""
            }`}
          >
            {subtitle}
          </p>
          {config.showGiftMessage && (
            <button
              type="button"
              onClick={onReadMessage}
              className="inline-flex cursor-pointer items-center gap-2.75 rounded-full border border-dark-dust/50 bg-white py-2.75 pl-4 pr-5 shadow-[0_1px_3px_rgba(51,51,51,0.05)]"
            >
              <span
                aria-hidden="true"
                className="h-8.5 w-8.5 shrink-0"
                style={{
                  backgroundColor: envelopeColor,
                  maskImage: `url(${GIFT_ENVELOPE_ICON})`,
                  WebkitMaskImage: `url(${GIFT_ENVELOPE_ICON})`,
                  maskSize: "contain",
                  WebkitMaskSize: "contain",
                  maskRepeat: "no-repeat",
                  WebkitMaskRepeat: "no-repeat",
                  maskPosition: "center",
                  WebkitMaskPosition: "center",
                }}
              />
              <span className="font-lato text-[12px] font-bold uppercase tracking-[1.4px] text-rodeo-black">
                Gift Message
              </span>
            </button>
          )}
        </div>

        {/* Right — banner card (+ play overlay when a video is set) */}
        {showHeroRight && (
          <>
            {!isMobile && <div className="w-px shrink-0 self-stretch bg-smoke" />}
            {/* Mobile: banner/video leads (prototype `.lp-hero-right { order: -1 }`)
                and pulls up over the section's top padding to sit flush under the
                header, full-bleed (see the `py-8.5` / `-mt-8.5` pairing above). */}
            <div
              className={`flex flex-col ${
                isMobile ? "-order-1 -mt-8.5 flex-auto" : "flex-[0_0_380px]"
              }`}
            >
              <div className={`overflow-hidden ${isMobile ? "rounded-none" : "rounded-md"}`}>
                {config.showVideo ? (
                  <button
                    type="button"
                    onClick={onPlayVideo}
                    aria-label="Play gift video message"
                    className={`${BANNER_BOX} cursor-pointer border-none p-0`}
                  >
                    <img
                      src={playCardSrc}
                      alt=""
                      className={BANNER_IMG}
                      onError={() => {
                        if (playCardSrc === thumbSrc) setFailedThumb(playCardSrc);
                      }}
                    />
                    <span className="absolute inset-0 z-2 flex items-center justify-center bg-black/15 text-white">
                      <PlayCircleOutline sx={{ fontSize: 40, opacity: 0.9 }} />
                    </span>
                  </button>
                ) : (
                  <div className={BANNER_BOX}>
                    <img src={bannerSrc} alt="" className={BANNER_IMG} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
